import { FaRegCircleCheck } from "react-icons/fa6";
import { FiShield, FiX } from "react-icons/fi";
import styled, { keyframes } from "styled-components";

import useMobileView from "../../../hooks/useMobileView";
import FlexBox from "../FlexBox";
import {
  BRICK_TERRACOTA,
  DAVYS_GREY,
  DAVYS_GREY_100,
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  PLUM_PURPLE,
  SUCCESS_GREEN,
} from "../colors";
import { Button } from "../Buttons";
import aiIntroIcon from "../../../assets/AiChat/ai-intro-icon.svg";
import { Body2 } from "../Headings";

const PROMPTS = [
  "Writing progress notes and summaries",
  "Brainstorming for case approaches",
  "Psychoeducation explanations",
  "Exploring patterns and key themes in sessions",
  "Getting details about your sessions or clients",
];

const REMINDERS = [
  "Amaha AI only uses existing client data, it doesn’t browse or pull external content.",
  "Always review, verify and interpret information based on your expertise and clinical judgement.",
];

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedSection = styled.div`
  opacity: 0;
  animation: ${slideUp} 700ms ease forwards;
  animation-delay: ${({ delay = 0 }) => delay}ms;
`;

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: block;
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${DAVYS_GREY_400};
    border-radius: 4px;
  }

  scrollbar-width: thin !important;
  -ms-overflow-style: auto !important;
`;

const Header = styled(FlexBox)`
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 1rem;
  background: ${PLUM_PURPLE[100]};
  flex-shrink: 0;
`;

const Content = styled(FlexBox)`
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding: 1.5rem 1rem;
`;

const IntroText = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(
    90deg,
    #624584 0%,
    ${BRICK_TERRACOTA[800]} 76.91%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;

  @media screen and (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SubText = styled.span`
  font-size: 0.875rem;
  color: ${DAVYS_GREY_700};
`;

const PromptBox = styled.div`
  background: ${DAVYS_GREY_100};
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.75rem;
  overflow: hidden;
`;

const PromptItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-top: ${({ first }) =>
    first ? "none" : `1px solid ${DAVYS_GREY_400}`};

  font-size: 0.875rem;
`;

const CheckSquare = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 2rem;
  height: 2rem;

  border-radius: 0.5rem;
  background: ${DAVYS_GREY_200};

  flex-shrink: 0;
`;

const ReminderBox = styled.div`
  padding: 1rem;
  background: ${DAVYS_GREY_100};
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.5rem;

  ul {
    margin: 0;
    padding-left: 1.25rem;
    color: ${DAVYS_GREY_800};
    font-size: 0.875rem;
  }

  li {
    line-height: 1.5rem;
  }

  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const Badge = styled(FlexBox)`
  align-items: center;
  gap: 0.75rem;

  padding: 0.75rem;
  border-radius: 0.5rem;

  background: ${SUCCESS_GREEN[100]};
  color: ${SUCCESS_GREEN[800]};

  font-size: 0.875rem;
`;

const ShieldIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 2rem;
  height: 2rem;

  border-radius: 0.375rem;
  background: ${SUCCESS_GREEN[700]};

  flex-shrink: 0;
`;

const AiChatIntroScreen = ({
  onClose = () => {},
  toggleIntroScreen = () => {},
}) => {
  const isMobile = useMobileView();

  return (
    <Container>
      <Header>
        <FlexBox
          width="100%"
          align="flex-start"
          justify="space-between"
          columnGap="1rem"
        >
          <FlexBox align="center" columnGap="1rem">
            <img src={aiIntroIcon} alt="AI Chat Icon" width={40} height={40} />

            <IntroText>Introducing Amaha Chat Assistant</IntroText>
          </FlexBox>

          {isMobile && (
            <FiX
              size={24}
              color={DAVYS_GREY_800}
              strokeWidth={2}
              style={{
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={onClose}
            />
          )}
        </FlexBox>

        <SubText>
          Use this space to ask questions about your client, session or
          platform.
        </SubText>
      </Header>

      <Content>
        <FlexBox column rowGap="1rem" width="100%">
          <AnimatedSection delay={500}>
            <FlexBox column rowGap="0.5rem" width="100%">
              <SubText>Use prompts to help you with:</SubText>

              <PromptBox>
                {PROMPTS.map((prompt, index) => (
                  <PromptItem key={prompt} first={index === 0}>
                    <CheckSquare>
                      <FaRegCircleCheck color={PLUM_PURPLE[800]} />
                    </CheckSquare>

                    <Body2 color={DAVYS_GREY[800]}>{prompt}</Body2>
                  </PromptItem>
                ))}
              </PromptBox>
            </FlexBox>
          </AnimatedSection>

          <AnimatedSection delay={2000}>
            <FlexBox column rowGap="1rem" width="100%">
              <FlexBox column rowGap="0.5rem" width="100%">
                <SubText>Important things to remember:</SubText>

                <ReminderBox>
                  <ul>
                    {REMINDERS.map(reminder => (
                      <li key={reminder}>{reminder}</li>
                    ))}
                  </ul>
                </ReminderBox>
              </FlexBox>

              <Badge>
                <ShieldIcon>
                  <FiShield color={DAVYS_GREY_100} strokeWidth={2} />
                </ShieldIcon>

                <span>Private • Context-aware • Built for clinicians.</span>
              </Badge>

              <FlexBox justify="center" margin="1rem 0 2rem 0" width="100%">
                <Button
                  onClick={toggleIntroScreen}
                  width="fit-content"
                  height="3rem"
                >
                  Get Started
                </Button>
              </FlexBox>
            </FlexBox>
          </AnimatedSection>
        </FlexBox>
      </Content>
    </Container>
  );
};

export default AiChatIntroScreen;
